import { faPause, faPlay, faUpload, faUndo, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@material-ui/core';
import { createKeyPath } from 'CMFW/context/helper/keyContext';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import { useForceRender } from 'hooks/useForceRender';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import WaveSurfer from 'wavesurfer.js';
import { IAudioDetails } from 'types/audio';
import AudioManagementModal from 'layout/modal/AudioManagementModal';
// @ts-ignore
import * as WaveSurferRegions from 'wavesurfer.js/dist/plugin/wavesurfer.regions';
// @ts-ignore
import * as WaveSurferTimeline from 'wavesurfer.js/dist/plugin/wavesurfer.timeline';

const REGION_END_PLAY_DURATION = 2;

const KEY = 'audio';
const KEY_START = 'audioStart';
const KEY_END = 'audioEnd';

const sToMs = (ms: number) => Math.floor(ms * 1000);

type IProps = {
  isModalOpen?: boolean; // Used for double binding with modal
};

const AudioWaveEditor: React.FC<IProps> = (props) => {
  // Use stable refs so they persist across renders
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const waveSurfer = useRef<WaveSurfer>();
  const region = useRef<any>();
  const [showManagementModal, setShowManagementModal] = useState(false);

  const forceRender = useForceRender();

  const parentKey = useContext(KeyContext);
  const aggregatedKeyName = createKeyPath(parentKey, KEY);
  const aggregatedKeyStartName = createKeyPath(parentKey, KEY_START);
  const aggregatedKeyEndName = createKeyPath(parentKey, KEY_END);

  const module = useContext(ModuleContext);

  // Get audio details from the audio key
  const audioDetails = module.getItemUpdatedValue(aggregatedKeyName) as IAudioDetails | null;
  const url = audioDetails?.path || null;
  const moduleStart = (module.getItemValue(aggregatedKeyStartName) || 0) as number;
  const moduleStartUpdated = (module.getItemUpdatedValue(aggregatedKeyStartName) || 0) as number;
  const moduleEnd = (module.getItemValue(aggregatedKeyEndName) || 0) as number;
  const moduleEndUpdated = (module.getItemUpdatedValue(aggregatedKeyEndName) || 0) as number;

  useEffect(() => {
    // Initialize or re-initialize WaveSurfer whenever a new URL is set
    if (waveformRef.current && url) {
      // Clean up any existing instance first
      if (waveSurfer.current) {
        waveSurfer.current.destroy();
      }

      waveSurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        plugins: [
          WaveSurferRegions.create({}),
          WaveSurferTimeline.create({ container: timelineRef.current, height: 10 }),
        ],
      });

      waveSurfer.current.load(url);

      waveSurfer.current.on('ready', () => {
        if (waveSurfer.current) {
          region.current = waveSurfer.current.addRegion({
            start: moduleStartUpdated / 1000,
            end: moduleEndUpdated > 0 ? moduleEndUpdated / 1000 : waveSurfer.current.getDuration(),
            drag: false,
          });
          region.current.on('click', (event: any) => {
            if (waveSurfer.current) {
              const waveformRect = waveSurfer.current.container.getBoundingClientRect();
              const progress = (event.clientX - waveformRect.x) / waveformRect.width;
              waveSurfer.current.seekTo(progress);
            }
          });
        }
      });
    }

    return () => {
      if (waveSurfer.current) {
        waveSurfer.current.destroy();
        // Ensure refs to region are cleared
        region.current = undefined;
      }
    };
    // Include url so we react to no-audio -> has-audio transitions
  }, [props.isModalOpen, aggregatedKeyName, url, moduleStartUpdated, moduleEndUpdated]);

  useEffect(() => {
    if (!waveSurfer.current) return;

    const handler = (event: any) => {
      const startMs = sToMs(Math.max(event.start, 0));
      module.updateItemValue(aggregatedKeyStartName, startMs);
      const endMs = sToMs(event.end);
      module.updateItemValue(aggregatedKeyEndName, endMs);
      forceRender();
    };

    waveSurfer.current.on('region-update-end', handler);

    return () => {
      // Remove handler if instance still alive
      if (waveSurfer.current) {
        // @ts-ignore wavesurfer provides `un` to remove listeners
        waveSurfer.current.un('region-update-end', handler);
      }
    };
  }, [props.isModalOpen, aggregatedKeyEndName, aggregatedKeyStartName, module, forceRender, url]);

  if (!url) {
    const canRecover = audioDetails?.canRecover || false;

    return (
      <>
        <Container $startChanged={false} $endChanged={false}>
          <NoAudioContainer>
            <NoAudioMessage>No audio available</NoAudioMessage>
            {audioDetails && audioDetails.ref !== 0 && (
              <ButtonStyled
                onClick={() => setShowManagementModal(true)}
                variant="contained"
                size="small"
                title={canRecover ? 'Click to recover audio' : 'Click to upload audio'}
              >
                <FontAwesomeIcon icon={canRecover ? faUndo : faUpload} />
              </ButtonStyled>
            )}
          </NoAudioContainer>
        </Container>

        {showManagementModal && audioDetails && audioDetails.ref !== 0 && (
          <AudioManagementModal
            audio={audioDetails}
            onClose={() => setShowManagementModal(false)}
            onAudioChange={(updatedAudio) => {
              // Update the audio data in the module
              module.updateItemValue(aggregatedKeyName, updatedAudio);
              forceRender();
            }}
          />
        )}
      </>
    );
  }

  const handleTogglePlaybackClick = () => {
    waveSurfer.current && waveSurfer.current.playPause();
  };

  const handlePlayRegionEndClick = () => {
    waveSurfer.current &&
      waveSurfer.current.play(moduleEndUpdated / 1000 - REGION_END_PLAY_DURATION, moduleEndUpdated / 1000);
  };

  const handlePlayRegionClick = () => {
    region.current && region.current.play();
  };

  const isInActiveSet = !!audioDetails?.audioSet?.active;

  return (
    <>
      <Container $startChanged={moduleStartUpdated !== moduleStart} $endChanged={moduleEndUpdated !== moduleEnd}>
        <WaveformWrapper ref={waveformRef} />
        <Timeline ref={timelineRef} />
        <ButtonStyled onClick={handleTogglePlaybackClick} variant="contained" size="small">
          <PlayPause icon={faPlay} />/<PlayPause icon={faPause} />
        </ButtonStyled>
        <ButtonStyled onClick={handlePlayRegionClick} variant="contained" size="small">
          Play Region
        </ButtonStyled>
        <ButtonStyled onClick={handlePlayRegionEndClick} variant="contained" size="small">
          Play Region End
        </ButtonStyled>
        {audioDetails && audioDetails.ref !== 0 && !isInActiveSet && (
          <ButtonStyled
            onClick={() => setShowManagementModal(true)}
            variant="contained"
            size="small"
            title="Manage audio"
          >
            <FontAwesomeIcon icon={faCog} />
          </ButtonStyled>
        )}
        {props.children}
      </Container>

      {showManagementModal && audioDetails && audioDetails.ref !== 0 && (
        <AudioManagementModal
          audio={audioDetails}
          onClose={() => setShowManagementModal(false)}
          onAudioChange={(updatedAudio) => {
            // Update the audio data in the module
            module.updateItemValue(aggregatedKeyName, updatedAudio);
            forceRender();
          }}
        />
      )}
    </>
  );
};

const Container = styled.div<{ $startChanged: boolean; $endChanged: boolean }>`
  ${(props) =>
    props.$startChanged &&
    css`
      & .wavesurfer-handle-start {
        background-color: rgb(251 113 113) !important;
      }
    `}
  ${(props) =>
    props.$endChanged &&
    css`
      & .wavesurfer-handle-end {
        background-color: rgb(251 113 113) !important;
      }
    `}
`;

const WaveformWrapper = styled.div``;

const Timeline = styled.div`
  margin-bottom: 5px;
`;

export const ButtonStyled = styled(Button)`
  height: 30px;
  && {
    margin-right: 5px;
    margin-bottom: 5px;
  }
`;

const PlayPause = styled(FontAwesomeIcon)`
  margin-left: 4px;
  margin-right: 4px;
`;

const NoAudioMessage = styled.div`
  color: #666;
  font-style: italic;
  padding: 8px;
`;

const NoAudioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export default AudioWaveEditor;
